import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCommonTimeSlots, bookCustomTimeSlot } from "../api/invitation";

const SlotSelector = () => {
	const [searchParams] = useSearchParams();
	const token = useSelector(state => state.auth.token);
    const navigate = useNavigate();

	const [slots, setSlots] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedSlot, setSelectedSlot] = useState(null);
    const [isBooking, setIsBooking] = useState(false);
	const slotsPerPage = 10;

	useEffect(() => {
		const fetchSlots = async () => {
			const inviteId = searchParams.get("invite_id");
			if (!inviteId) {
				console.error("Invite ID not found in query parameters");
				return;
			}

			const startTime = moment().add(1, "hour").unix();
			const endTime = moment().add(2, "weeks").unix();
			const tz = moment.tz.guess();

			try {
				const fetchedSlots = await getCommonTimeSlots(token, inviteId, startTime, endTime, tz);
				setSlots(fetchedSlots);
			} catch (error) {
				console.error("Error fetching slots:", error);
			}
		};

		fetchSlots();
	}, [searchParams, token]);

	const indexOfLastSlot = currentPage * slotsPerPage;
	const indexOfFirstSlot = indexOfLastSlot - slotsPerPage;
	const currentSlots = slots.slice(indexOfFirstSlot, indexOfLastSlot);

	const handleNextPage = () => {
		if (indexOfLastSlot < slots.length) {
			setCurrentPage(prevPage => prevPage + 1);
		}
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage(prevPage => prevPage - 1);
		}
	};

	const handleBookSlot = async () => {
		if (selectedSlot) {
			setIsBooking(true);
			try {
				const inviteId = searchParams.get("invite_id");
				await bookCustomTimeSlot(token, inviteId, selectedSlot.start, selectedSlot.end);
				navigate(`/meeting-confirmation?id=${inviteId}`);
			} catch (error) {
				console.error("Error booking slot:", error);
				alert(error.message || "Failed to book slot");
			} finally {
				setIsBooking(false);
			}
		}
	};

	return (
		<div style={styles.container}>
			<header style={styles.header}>
				<h1 style={styles.headerTitle}>Meeting Slot Selector</h1>
				<p style={styles.headerDescription}>Choose a time slot that works best for you. Click on a slot to view details and book it.</p>
			</header>
			<div style={{ ...styles.layout, paddingBottom: window.innerWidth <= 768 ? "80px" : "0" }}>
				<div style={styles.leftColumn}>
					<div style={styles.grid}>
						{currentSlots.map((slot, index) => (
							<div
								key={index}
								style={{
									...styles.card,
									...(selectedSlot === slot ? styles.selectedCard : {}),
								}}
								onClick={() => setSelectedSlot(slot)}>
								<h3 style={styles.cardTitle}>Slot {index + 1 + (currentPage - 1) * slotsPerPage}</h3>
								<p style={styles.cardText}>
									<strong>Start:</strong> {moment.unix(slot.start).format("LLLL")}
								</p>
								<p style={styles.cardText}>
									<strong>End:</strong> {moment.unix(slot.end).format("LLLL")}
								</p>
							</div>
						))}
					</div>
					<div style={styles.pagination}>
						<button
							style={{
								...styles.paginationButton,
								...(currentPage === 1 ? styles.paginationButtonDisabled : {}),
							}}
							onClick={handlePreviousPage}
							disabled={currentPage === 1}>
							Previous
						</button>
						<button
							style={{
								...styles.paginationButton,
								...(indexOfLastSlot >= slots.length ? styles.paginationButtonDisabled : {}),
							}}
							onClick={handleNextPage}
							disabled={indexOfLastSlot >= slots.length}>
							Next
						</button>
					</div>
				</div>

				{window.innerWidth <= 768 ? (
					<div style={styles.footerMobile}>
						{selectedSlot ? (
							<div style={styles.footerContainer}>
								<div style={styles.footerInfo}>
									<h2 style={styles.footerTitle}>Selected Slot</h2>
									<div style={styles.footerDetails}>
										<span style={styles.footerTime}>
											{moment.unix(selectedSlot.start).format("LT")} - {moment.unix(selectedSlot.end).format("LT")}
										</span>
										<span style={styles.footerDate}>{moment.unix(selectedSlot.start).format("MMM D, YYYY")}</span>
									</div>
								</div>
								<button
									style={{
										...styles.bookButtonMobile,
										...(isBooking ? styles.bookButtonDisabled : {}),
									}}
									onClick={handleBookSlot}
									disabled={isBooking}>
									{isBooking ? "Booking..." : "Book Now"}
								</button>
							</div>
						) : (
							<p style={styles.noSelectionText}>Select a time slot to continue</p>
						)}
					</div>
				) : (
					<div style={styles.rightColumn}>
						{selectedSlot ? (
							<div style={styles.rightColumnContent}>
								<h2 style={styles.rightColumnTitle}>Selected Slot</h2>
								<p style={styles.rightColumnText}>
									<strong>Start:</strong> {moment.unix(selectedSlot.start).format("LLLL")}
								</p>
								<p style={styles.rightColumnText}>
									<strong>End:</strong> {moment.unix(selectedSlot.end).format("LLLL")}
								</p>
								<button style={styles.bookButton} onClick={handleBookSlot} disabled={isBooking}>
									{isBooking ? "Booking..." : "Book Slot"}
								</button>
							</div>
						) : (
							<p style={styles.noSelectionText}>Click on a slot to view details here.</p>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

const styles = {
	container: {
		padding: "20px",
		maxWidth: "1200px",
		margin: "0 auto",
	},
	header: {
		textAlign: "center",
		marginBottom: "30px",
	},
	headerTitle: {
		fontSize: "2.5rem",
		color: "#333",
		marginBottom: "10px",
	},
	headerDescription: {
		fontSize: "1.2rem",
		color: "#555",
	},
	layout: {
		display: "flex",
		gap: "20px",
		alignItems: "flex-start",
	},
	leftColumn: {
		flex: 1,
		width: "100%",
	},
	grid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
		gap: "20px",
		marginBottom: "20px",
	},
	card: {
		border: "1px solid #e0e0e0",
		borderRadius: "8px",
		padding: "20px",
		backgroundColor: "#fff",
		cursor: "pointer",
		transition: "all 0.2s ease",
		"&:hover": {
			boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
		},
	},
	selectedCard: {
		border: "2px solid #007BFF",
		boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
	},
	cardTitle: {
		fontSize: "1.25rem",
		color: "#333",
		marginBottom: "10px",
	},
	cardText: {
		fontSize: "0.9rem",
		color: "#666",
		marginBottom: "5px",
	},
	rightColumn: {
		width: "300px",
		flexShrink: 0,
		position: "sticky",
		top: "20px",
	},
	rightColumnContent: {
		padding: "20px",
		backgroundColor: "#fff",
		borderRadius: "8px",
		boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
	},
	rightColumnTitle: {
		fontSize: "1.5rem",
		color: "#333",
		marginBottom: "15px",
	},
	rightColumnText: {
		fontSize: "0.9rem",
		color: "#666",
		marginBottom: "10px",
	},
	footerMobile: {
		position: "fixed",
		bottom: 0,
		left: 0,
		right: 0,
		backgroundColor: "#fff",
		borderTop: "1px solid #e0e0e0",
		boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
		zIndex: 1000,
	},
	footerContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "12px 16px",
		maxWidth: "1200px",
		margin: "0 auto",
	},
	footerInfo: {
		flex: 1,
	},
	footerTitle: {
		fontSize: "0.8rem",
		color: "#666",
		marginBottom: "4px",
	},
	footerDetails: {
		display: "flex",
		flexDirection: "column",
	},
	footerTime: {
		fontSize: "1.1rem",
		color: "#333",
		fontWeight: "600",
	},
	footerDate: {
		fontSize: "0.9rem",
		color: "#666",
	},
	bookButton: {
		backgroundColor: "#007BFF",
		color: "#fff",
		border: "none",
		borderRadius: "6px",
		padding: "12px 24px",
		fontSize: "1rem",
		fontWeight: "500",
		cursor: "pointer",
		transition: "background-color 0.2s",
		width: "100%",
		marginTop: "15px",
		"&:hover": {
			backgroundColor: "#0056b3",
		},
	},
	bookButtonMobile: {
		backgroundColor: "#007BFF",
		color: "#fff",
		border: "none",
		borderRadius: "6px",
		padding: "12px 24px",
		fontSize: "1rem",
		fontWeight: "500",
		cursor: "pointer",
		transition: "background-color 0.2s",
		marginLeft: "16px",
		whiteSpace: "nowrap",
		"&:hover": {
			backgroundColor: "#0056b3",
		},
	},
	pagination: {
		display: "flex",
		justifyContent: "center",
		gap: "10px",
		marginTop: "20px",
	},
	paginationButton: {
		backgroundColor: "#007BFF",
		color: "#fff",
		border: "none",
		borderRadius: "6px",
		padding: "8px 16px",
		fontSize: "0.9rem",
		cursor: "pointer",
		transition: "background-color 0.2s",
		"&:hover": {
			backgroundColor: "#0056b3",
		},
	},
	paginationButtonDisabled: {
		backgroundColor: "#ccc",
		cursor: "not-allowed",
		"&:hover": {
			backgroundColor: "#ccc",
		},
	},
	noSelectionText: {
		fontSize: "0.9rem",
		color: "#666",
		textAlign: "center",
		padding: "12px",
	},
	bookButtonDisabled: {
		backgroundColor: "#80bfff",
		cursor: "not-allowed",
		"&:hover": {
			backgroundColor: "#80bfff",
		},
	},
};

export default SlotSelector;
